<template>
    <div
        @dragover.prevent="dragging = true"
        @dragenter.prevent="onDragEnter"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
        :class="
            dragging
                ? 'border-minplan-blue bg-minplan-beige/50'
                : 'border-minplan-darkbeige/40'
        "
        class="border-2 border-dashed rounded-lg p-3 transition-colors"
    >
        <!-- Thema 11: Wiederverwendbare Ablagezone. Legt der Nutzer eine Datei ab,
             wird sie per @file an den Host gemeldet — Klicken/Auswaehlen im Slot
             bleibt zusaetzlich moeglich. Validierung/Handling macht der Host (ein
             Weg, egal ob gezogen oder geklickt). -->
        <slot />
        <p
            class="mt-2 text-center text-xs"
            :class="dragging ? 'text-minplan-blue' : 'text-minplan-darkbeige'"
        >
            {{ dragging ? $t("forms.dropFileActive") : hint || $t("forms.dropFileHint") }}
        </p>
    </div>
</template>

<script>
import { defineComponent } from "vue";

// R3 Punkt 8 (Welle-1-Nitpicks): `defineComponent`-Form (Norm für 95 von 148
// Pages/Komponenten mit script-Block, s. `CONVENTIONS-IST.md` Kap. 2) und
// Zähler statt Flag für `dragenter`/`dragleave` — verschachtelte Kind-
// Elemente im Slot lassen `dragleave` beim Übergang zwischen Eltern- und
// Kind-Element bubbeln, ein blosses Boolean würde die Hervorhebung dabei
// kurz fälschlich beenden. Der Zähler bleibt > 0, solange der Zeiger
// irgendwo innerhalb der Zone ist.
export default defineComponent({
    name: "FileDropzone",
    props: {
        hint: { type: String, default: "" },
        // R3 Punkt 7 (BACKLOG §5 Welle-1-Folgeauftrag): optionale Whitelist im
        // Format des nativen `accept`-Attributs (`.pdf`, `image/*`,
        // `application/pdf` — komma-getrennt). Leer = wie bisher jede Datei.
        // Vorher liess der Drop-Pfad JEDE Datei durch und erst der Server
        // wies eine falsche Endung ab; die Host-Inputs filtern `accept` seit
        // je her nur den Auswahl-DIALOG, nie den Drop.
        accept: { type: String, default: "" },
    },
    // W2 (Fix-Runde 1): eine per Drop abgelegte, nicht zur `accept`-Liste
    // passende Datei feuerte bisher still gar nichts — der Host konnte das
    // nicht von einem Drop ohne Datei unterscheiden. `reject` meldet den
    // Fall explizit, damit Hosts eine Fehlermeldung zeigen können.
    emits: ["file", "reject"],
    data() {
        return { dragging: false, dragCounter: 0 };
    },
    methods: {
        onDragEnter() {
            this.dragCounter++;
            this.dragging = true;
        },
        onDragLeave() {
            this.dragCounter = Math.max(0, this.dragCounter - 1);
            if (this.dragCounter === 0) {
                this.dragging = false;
            }
        },
        matchesAccept(file) {
            const patterns = this.accept
                .split(",")
                .map((pattern) => pattern.trim().toLowerCase())
                .filter(Boolean);
            if (patterns.length === 0) return true;

            const name = (file.name || "").toLowerCase();
            const type = (file.type || "").toLowerCase();

            return patterns.some((pattern) => {
                if (pattern.startsWith(".")) {
                    return name.endsWith(pattern);
                }
                if (pattern.endsWith("/*")) {
                    return type.startsWith(pattern.slice(0, -1));
                }
                if (type === pattern) return true;

                // W2 (Fix-Runde 1): manche Browser/OS-Kombinationen (z. B.
                // Chrome unter Windows) melden fuer bekannte Endungen einen
                // abweichenden MIME-Typ (`.zip` als
                // "application/x-zip-compressed" statt "application/zip")
                // — der Subtyp der Pattern-MIME dient als Endungs-Fallback,
                // damit eine per `accept="application/zip"` erlaubte Datei
                // nicht an einem browserspezifischen MIME-String scheitert.
                const subtype = pattern.split("/")[1];
                return Boolean(subtype) && name.endsWith(`.${subtype}`);
            });
        },
        onDrop(event) {
            this.dragCounter = 0;
            this.dragging = false;
            const file =
                event.dataTransfer && event.dataTransfer.files
                    ? event.dataTransfer.files[0]
                    : null;
            if (!file) return;

            if (this.matchesAccept(file)) {
                this.$emit("file", file);
            } else {
                this.$emit("reject", file);
            }
        },
    },
});
</script>

<style scoped></style>
